<template>
    <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
            <span>{{ title }}</span>
            <v-chip small outlined>{{ items.length }} {{ $t('kohdetta') }}</v-chip>
        </v-card-title>

        <v-card-subtitle>
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn small color="primary" v-bind="attrs" v-on="on" :disabled="loading">
                        <v-icon left small>{{ icons.mdiPlus }}</v-icon>
                        {{ $t('Lisää') }}
                        <v-icon right small>{{ icons.mdiChevronDown }}</v-icon>
                    </v-btn>
                </template>
                <v-list dense>
                    <v-list-item @click="$emit('upload')" :disabled="loading">
                        <v-list-item-icon>
                            <v-icon small>{{ icons.mdiUpload }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ $t('Lataa kuva') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="$emit('addFromLibrary')" :disabled="loading">
                        <v-list-item-icon>
                            <v-icon small>{{ icons.mdiImageMultiple }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ $t('Kirjastosta') }}</v-list-item-title>
                    </v-list-item>

                    <v-list-item v-if="position != 2" @click="$emit('addNews')" :disabled="loading">
                        <v-list-item-icon>
                            <v-icon small>{{ icons.mdiNewspaper }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ $t('Uutiset') }} </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="$emit('addEvents')" :disabled="loading">
                        <v-list-item-icon>
                            <v-icon small>{{ icons.mdiCalendar }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ $t('Tapahtumat') }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-card-subtitle>

        <v-divider></v-divider>

        <v-card-text>
            <!-- Empty State -->
            <div v-if="items.length === 0" class="text-center py-8">
                <v-icon size="64" color="grey lighten-2">{{ icons.mdiImageOff }}</v-icon>
                <p class="text-h6 grey--text mt-4">{{ $t('Ei sisältöä') }}</p>
                <p class="grey--text">{{ $t('Lisää sisältöä yllä olevilla painikkeilla') }}</p>
            </div>

            <!-- Content List -->
            <draggable v-else v-model="localItems" :disabled="loading" handle=".drag-handle" @end="handleDragEnd"
                ghost-class="ghost-card">
                <v-card v-for="item in localItems" :key="item.id" class="mb-3" outlined>
                    <v-card-text class="d-flex align-center">
                        <!-- Drag Handle -->
                        <v-icon class="drag-handle mr-3" :class="{ 'grey--text': loading }">
                            {{ icons.mdiDragVertical }}
                        </v-icon>

                        <!-- Content Type Icon -->
                        <div class="mr-3">
                            <ContentTypeIcon :content-type="item.content_type" />
                        </div>

                        <!-- Content Info -->
                        <div class="flex-grow-1">
                            <div class="font-weight-medium">{{ item.title }}</div>
                            <div class="text-caption grey--text">
                                {{ getContentTypeName(item.content_type) }}
                            </div>
                        </div>

                        <!-- Image Thumbnail (if image content) -->
                        <div v-if="item.content_type === 2 && item.image_url" class="mr-3">
                            <v-img :src="item.image_url" max-width="60" max-height="40" contain></v-img>
                        </div>

                        <!-- Actions -->
                        <v-btn small color="error" @click="$emit('remove', item.id)" :disabled="loading">
                            <v-icon small>{{ icons.mdiDelete }}</v-icon>
                        </v-btn>
                    </v-card-text>

                    <!-- News Preview (for content_type=1) -->
                    <v-expansion-panels v-if="item.content_type === 1" flat class="news-preview-panel">
                        <v-expansion-panel>
                            <v-expansion-panel-header class="py-2 px-4">
                                <span class="text-caption">
                                    <v-icon small class="mr-1">{{ icons.mdiNewspaper }}</v-icon>
                                    {{ newsPreview.length }} {{ $t('aktiivista uutista.') }}
                                    {{ $t('Näytetään max 5 uusinta uutista') }}
                                </span>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-list v-if="newsPreview.length > 0" dense class="py-0 mt-2">
                                    <v-list-item v-for="news in newsPreview" :key="news.id" class="px-0">
                                        <v-list-item-content>
                                            <v-list-item-title class="text-body-2">{{ news.title }}</v-list-item-title>
                                            <v-list-item-subtitle class="text-caption">
                                                {{ formatDate(news.release_date) }}
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                        <v-list-item-action class="my-0">
                                            <v-btn icon x-small
                                                :to="{ name: 'admin-news-form', params: { id: news.id } }">
                                                <v-icon small>{{ icons.mdiPencil }}</v-icon>
                                            </v-btn>
                                        </v-list-item-action>
                                    </v-list-item>
                                </v-list>
                                <div v-else class="text-caption grey--text py-2">
                                    {{ $t('Ei aktiivisia uutisia') }}
                                </div>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-card>
            </draggable>
        </v-card-text>
    </v-card>
</template>

<script>
import { ref, watch } from '@vue/composition-api'
import draggable from 'vuedraggable'
import ContentTypeIcon from './ContentTypeIcon.vue'
import {
    mdiUpload,
    mdiImageMultiple,
    mdiNewspaper,
    mdiCalendar,
    mdiImageOff,
    mdiDragVertical,
    mdiDelete,
    mdiPlus,
    mdiChevronDown,
    mdiPencil
} from '@mdi/js'
import { useTranslations } from '@/@core/libs/i18n';
import { i18n } from '@/plugins/i18n';

export default {
    name: 'ContentList',
    components: {
        draggable,
        ContentTypeIcon
    },
    props: {
        title: {
            type: String,
            required: true
        },
        items: {
            type: Array,
            default: () => []
        },
        position: {
            type: Number,
            required: true
        },
        screenId: {
            type: [String, Number],
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        newsPreview: {
            type: Array,
            default: () => []
        }
    },
    setup(props, { emit }) {
        const localItems = ref([...props.items])

        // Watch for external changes to items
        watch(() => props.items, (newItems) => {
            localItems.value = [...newItems]
        }, { deep: true })

        const handleDragEnd = () => {
            // Emit reorder event with position and new order
            emit('reorder', props.position, localItems.value)
        }

        const getContentTypeName = (contentType) => {
            const types = {
                1: i18n.t('Uutiset'),
                2: i18n.t('Kuva'),
                3: i18n.t('Video'),
                4: i18n.t('Tapahtumat')
            }
            return types[contentType] || i18n.t('Tuntematon')
        }

        const formatDate = (dateString) => {
            if (!dateString) return ''
            const date = new Date(dateString)
            return date.toLocaleDateString('fi-FI', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric'
            })
        }

        return {
            localItems,
            handleDragEnd,
            getContentTypeName,
            formatDate,
            icons: {
                mdiUpload,
                mdiImageMultiple,
                mdiNewspaper,
                mdiCalendar,
                mdiImageOff,
                mdiDragVertical,
                mdiDelete,
                mdiPlus,
                mdiChevronDown,
                mdiPencil
            }
        }
    }
}
</script>

<style scoped>
.drag-handle {
    cursor: move;
}

.drag-handle.grey--text {
    cursor: not-allowed;
}

.ghost-card {
    opacity: 0.5;
    background-color: #f5f5f5;
}

.news-preview-panel {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.news-preview-panel .v-expansion-panel::before {
    box-shadow: none;
}

.news-preview-panel .v-expansion-panel-header {
    min-height: 36px;
    background-color: #fafafa;
}

.news-preview-panel .v-expansion-panel-content__wrap {
    padding: 8px 16px;
}
</style>
